import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Timer } from 'lucide-react';
import type { RootState } from '../../../../store/redux/store/store';
import { getCompanyRules, updateCompanyRules } from '../../../../api/General/Company';
import { updateCompanyRules as updateCompanyRulesAction } from '../../../../store/redux/actions/userActionCreator';
import { GetCompanyGrossPays } from '../../../../api/Payroll/grossPay';
import type { CompanyGrossPayView } from '../../../../models/payroll';
import {
  OvertimeNoManagerRouting,
  PayrollSalaryBaseMode,
  PAYROLL_BASIS_DEFAULTS,
  PAYROLL_OVERTIME_REGULAR_MIN,
  PAYROLL_OVERTIME_PREMIUM_MIN,
} from '../../../../models/general';
import type { CompanyRulesInput } from '../../../../models/general';
import { apiErrorsOf } from '../../../../utils/requestHandling';
import { useComplianceCountry } from '../../../../hooks/useComplianceCountry';
import { Switch } from '../../../../components/ui/Switch';
import Button from '../../../../components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '../../../../components/ui/Dialog';
import { Spinner } from '../../../../components/ui/Spinner';
import StickyActionBar from '../../../../components/ui/StickyActionBar';
import { PageLayout } from '../../../../components/ui/PageLayout';
import AppIcon from '../../../../components/AppIcon';
import { cn } from '../../../../utils/cn';
import { isBasicSalaryComponent } from '../../../../models/payroll';

/**
 * HR-facing editor for everything that turns ATTENDANCE HOURS INTO PAY: whether attendance overtime
 * reaches payroll at all, whether it has to be approved first, which salary base it is priced from,
 * the two statutory multipliers, and the hourly-rate divisor.
 *
 * WHY THIS PAGE EXISTS (CO-304): these controls used to live on
 * Settings/Payroll/ComplianceSettings, whose route is registered only for UAE/KSA tenants
 * (`App.tsx`, guarded on `complianceCountry`) and whose nav entry is filtered the same way
 * (`hooks/useFilteredNavigation.ts` SHARED_COMPLIANCE_PATHS). On dev that is ~843 of 956 companies
 * with NO route to the screen, i.e. most tenants could not configure whether their overtime is paid.
 * This page carries no country condition. Only the genuinely statutory settings (GOSI/CCHI
 * schedules, the end-of-service base) stayed behind on the compliance page.
 *
 * 🔴 THE POST SENDS THE WHOLE CompanyRulesView BACK, NOT JUST THESE SIX FIELDS. CompanyRulesInput
 * has non-nullable members (attendanceImageConfig, breakConfig, timesheetCreationDay, the
 * scheduledShift* thresholds ...) that CompanyService.UpdateCompanyRules assigns
 * UNCONDITIONALLY, so a six-field POST would deserialize those to their C# defaults and wipe the
 * tenant's attendance configuration. Same round-trip pattern as EngagementJobsTab and
 * Settings/Attendance/workscheduleconfig.tsx. Only the fields below are ever mutated locally, and
 * `payrollEosBaseMode` rides along untouched from the GET so this page cannot disturb it.
 *
 * Every field is nullable on the wire and null means "server default", so the form reads through
 * PAYROLL_BASIS_DEFAULTS with `??` only. The base mode is a STRING enum ('Auto' / 'BasicOnly' /
 * 'Gross') because Program.cs registers a global Newtonsoft StringEnumConverter, so 'Auto' is a
 * real configured value and must never be treated as unset by a truthiness test. Controls are
 * native inputs rather than the shared `Input.jsx`, which is untyped and would fail the tsc
 * baseline gate from a new file.
 *
 * 🔴 CO-304 — the approval requirement (`payrollRequireOvertimeApproval`) is a TRISTATE, not a
 * boolean: true = only approved overtime is paid, false = an explicit opt-out, null = never
 * configured. Null resolves to OFF today (PayrollSettings.RequiresOvertimeApprovalOn), so the two
 * off-states pay identically, but they are shown apart because only the explicit one is a decision
 * this tenant made. `clearPayrollRequireOvertimeApproval` is the only way back to null.
 *
 * 🔴 Arming it is refused TWICE by the server, and both refusals are 400s carrying the whole
 * explanation:
 *   Gate 1 — employees with no possible approver. A hard block, rendered as a fix-this-first
 *            callout, never as a toast: nothing is retryable until the data changes.
 *   Gate 2 — the impact acknowledgement. The server measures the shifts whose overtime would stop
 *            being paid and refuses until the input carries
 *            `acknowledgeOvertimeApprovalGateImpact: true`. That flag is sent ONLY after a human
 *            read the server's own message in the confirmation dialog, which is why `handleSave`
 *            takes the acknowledgement as an explicit argument defaulted to false. Sending it
 *            pre-emptively would delete the gate.
 * Both messages are composed and measured by the server, so they are rendered VERBATIM; only the
 * chrome around them is translated.
 *
 * 🔴 CO-348 — `payrollNoManagerOvertimeApproval` is the second tristate on this page and it is a
 * CHILD of the requirement above: it only decides anything while approval is required, so it is
 * nested under that switch and locked whenever the parent is off, the same way the overtime rate
 * section is locked by the master switch. Its two members are NOT symmetrical — RouteToHr keeps a
 * human in the loop and is what null resolves to, AutoApprove pays overtime nobody looked at — so
 * the default is never presented as a toggle whose off-state happens to be safe. Choosing
 * AutoApprove also relaxes Gate 1 server-side (nobody is undecidable once nobody has to decide),
 * which is why the Gate 1 callout points at it.
 *
 * 🔴 CO-348 (review D2) — that relaxation is SYMMETRIC. Moving the routing back off AutoApprove
 * while approval is still required re-runs Gate 1, so this page can receive a Gate 1 refusal on a
 * save that is not an arming save; `routeRefusal` therefore keys Gate 1 on the message, not on
 * `arming`. Gate 2 stays keyed on `arming` because it measures what stops being paid when the
 * requirement is switched ON, which is not what that save changes.
 *
 * 🔴 CO-348 (review D1) — the "Decides nothing until overtime has to be approved" lock on this
 * control is now enforced by the SERVER as well (AttendanceService.RaiseOvertimeApprovalRequestAsync
 * consults PayrollSettings.RequiresOvertimeApprovalOn before the auto-approve branch). It used to be
 * a UI-only claim while the pipeline auto-approved regardless, which also meant a direct
 * POST /Company/UpdateCompanyRules or /Company/OperatorUpdateCompanyRules could move money on a
 * tenant that never armed the gate. The copy stands for PAY because it is now true.
 * ⚠️ CO-348 (re-verification P3) — but it OVERSTATED the inertness, so it is no longer shown
 * unconditionally. A saved AutoApprove decides nothing about money or about the approval queue
 * while the gate is off, yet it DOES still pre-disable Gate 1 on the next arming request
 * (CompanyService `noManagerOvertimeAutoApproves` -> `undecidableEmployees = 0`). That case now
 * renders `noManagerRoutingInertAuto` instead, which says so.
 *
 * 🔴 A 400 REJECTS the promise — `utils/request.ts` runs every non-2xx through `errorHandling`,
 * which rejects with the parsed `Result` body. So a refusal never arrives as a resolved
 * `result.errors`; both gates are read out of the CATCH via `apiErrorsOf`. The resolved branch is
 * still checked because it costs nothing and covers a 200-with-errors envelope.
 */

const INPUT_CLASSES =
  'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm tabular-nums ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary ' +
  'transition-colors disabled:cursor-not-allowed disabled:opacity-50';

interface AttendancePayForm {
  useAttendanceOvertime: boolean;
  /** CO-304 tristate: true = required, false = explicitly not required, null = not configured. */
  requireApproval: boolean | null;
  /** CO-348 tristate: null = not configured, which resolves to RouteToHr. */
  noManagerRouting: OvertimeNoManagerRouting | null;
  overtimeBaseMode: PayrollSalaryBaseMode;
  /** Kept as raw strings so a half-typed "1." is not silently coerced while the user types. */
  regularMultiplier: string;
  premiumMultiplier: string;
  hoursPerMonth: string;
}

const BASE_MODES: PayrollSalaryBaseMode[] = [
  PayrollSalaryBaseMode.Auto,
  PayrollSalaryBaseMode.BasicOnly,
  PayrollSalaryBaseMode.Gross,
];

/** Safe member first, so the option that keeps a human in the loop is the one read first. */
const NO_MANAGER_ROUTINGS: OvertimeNoManagerRouting[] = [
  OvertimeNoManagerRouting.RouteToHr,
  OvertimeNoManagerRouting.AutoApprove,
];

const AttendancePaySettings: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const companyId = useSelector((state: RootState) => state.user.company?.id);
  const complianceCountry = useComplianceCountry();
  // The compliance page is registered for UAE/KSA only, so linking to it from a tenant that has no
  // compliance country would be a link to a route that does not exist — the exact asymmetry this
  // page was created to end.
  const complianceReachable = complianceCountry === 'UAE' || complianceCountry === 'KSA';

  const [rules, setRules] = useState<CompanyRulesInput | null>(null);
  const [form, setForm] = useState<AttendancePayForm | null>(null);
  const [baseline, setBaseline] = useState<AttendancePayForm | null>(null);
  const [basicComponents, setBasicComponents] = useState<CompanyGrossPayView[]>([]);
  const [componentsKnown, setComponentsKnown] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  /** Gate 1 — the server's undecidable-employees refusal. Sticky: only an edit clears it. */
  const [approverBlock, setApproverBlock] = useState<string | null>(null);
  /** Gate 2 — the server's measured impact message, shown in the confirmation dialog. */
  const [impactMessage, setImpactMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!companyId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    setSaved(false);
    setApproverBlock(null);
    setImpactMessage(null);
    try {
      const [rulesResult, grossPays] = await Promise.all([
        getCompanyRules(companyId),
        GetCompanyGrossPays().catch(() => null),
      ]);

      if (rulesResult.errors?.length || !rulesResult.payload) {
        setError(
          rulesResult.errors?.[0]?.description ||
            t('settings.payrollBasis.loadFailed', 'Could not load the payroll calculation basis.')
        );
        return;
      }

      const payload = rulesResult.payload;
      const loaded: AttendancePayForm = {
        useAttendanceOvertime:
          payload.payrollUseAttendanceOvertime ?? PAYROLL_BASIS_DEFAULTS.useAttendanceOvertime,
        requireApproval: payload.payrollRequireOvertimeApproval ?? null,
        noManagerRouting: payload.payrollNoManagerOvertimeApproval ?? null,
        overtimeBaseMode: payload.payrollOvertimeBaseMode ?? PAYROLL_BASIS_DEFAULTS.overtimeBaseMode,
        regularMultiplier: String(
          payload.payrollOvertimeRegularMultiplier ?? PAYROLL_BASIS_DEFAULTS.overtimeRegularMultiplier
        ),
        premiumMultiplier: String(
          payload.payrollOvertimePremiumMultiplier ?? PAYROLL_BASIS_DEFAULTS.overtimePremiumMultiplier
        ),
        hoursPerMonth: String(payload.payrollHoursPerMonth ?? PAYROLL_BASIS_DEFAULTS.hoursPerMonth),
      };

      setRules(payload as unknown as CompanyRulesInput);
      setForm(loaded);
      setBaseline(loaded);

      if (grossPays && grossPays.payload) {
        setBasicComponents(grossPays.payload.filter(isBasicSalaryComponent));
        setComponentsKnown(true);
      }
    } catch (err) {
      console.error('Failed to load attendance pay settings:', err);
      setError(t('settings.payrollBasis.loadFailed', 'Could not load the payroll calculation basis.'));
    } finally {
      setLoading(false);
    }
  }, [companyId, t]);

  useEffect(() => {
    load();
  }, [load]);

  const patch = useCallback((changes: Partial<AttendancePayForm>) => {
    setForm((prev) => (prev ? { ...prev, ...changes } : prev));
    setSaved(false);
    // Both server refusals describe the state that was just abandoned, so they must not outlive an
    // edit — a stale "N employees have no approver" under a switch the user has since turned off
    // would read as a live block.
    setApproverBlock(null);
    setImpactMessage(null);
  }, []);

  // Live rule evaluation. Each entry is one line of the checklist under its field, so the user
  // sees the statutory floor pass or fail as they type rather than after a server rejection.
  const checks = useMemo(() => {
    const regular = form ? Number(form.regularMultiplier) : NaN;
    const premium = form ? Number(form.premiumMultiplier) : NaN;
    const hours = form ? Number(form.hoursPerMonth) : NaN;
    return {
      regularNumeric: Number.isFinite(regular),
      regularMin: Number.isFinite(regular) && regular >= PAYROLL_OVERTIME_REGULAR_MIN,
      premiumNumeric: Number.isFinite(premium),
      premiumMin: Number.isFinite(premium) && premium >= PAYROLL_OVERTIME_PREMIUM_MIN,
      premiumAboveRegular:
        Number.isFinite(premium) && Number.isFinite(regular) ? premium >= regular : true,
      hoursWhole: Number.isInteger(hours),
      hoursPositive: Number.isFinite(hours) && hours > 0,
    };
  }, [form]);

  const hasErrors = useMemo(() => Object.values(checks).some((ok) => !ok), [checks]);

  const dirty = useMemo(() => {
    if (!form || !baseline) return false;
    return (Object.keys(baseline) as (keyof AttendancePayForm)[]).some((k) => baseline[k] !== form[k]);
  }, [form, baseline]);

  /** True only while this save would flip the requirement from not-armed to armed. */
  const arming = !!form && form.requireApproval === true && baseline?.requireApproval !== true;

  /**
   * Routes one refusal to the surface that matches it. Returns true when it was handled here.
   * `source` is whatever the API layer produced: a resolved Result, or the rejected body.
   */
  const routeRefusal = (source: unknown): boolean => {
    const description = apiErrorsOf(source)[0]?.description ?? '';
    if (!description) return false;
    // Gate 2 — identified by the flag the server tells us to send back, the one token that cannot
    // appear in any other refusal.
    if (arming && /AcknowledgeOvertimeApprovalGateImpact/i.test(description)) {
      setImpactMessage(description);
      return true;
    }
    // Gate 1 — no possible approver. An instruction to act on, not a failure to retry.
    // 🔴 CO-348 (review D2) — NOT guarded by `arming`. The server now runs the same preflight when
    // the no-manager routing moves back OFF auto-approve while approval is ALREADY required, and
    // that save is not an arming save. Matched on the server's own leading clause, of which there
    // are exactly two, so a gate-1 refusal from either trigger lands in the callout instead of
    // falling through to a generic toast the user cannot act on.
    if (/Cannot require overtime approval yet|Cannot route no-manager overtime to HR yet/i.test(description)) {
      setImpactMessage(null);
      setApproverBlock(description);
      return true;
    }
    // Anything else: close the confirmation dialog first, or the failure renders behind it.
    setImpactMessage(null);
    setError(description);
    return true;
  };

  /**
   * @param acknowledgeImpact ONLY ever true when it came from the confirmation dialog, i.e. after a
   * human read the server's measured impact. Never defaulted, never inferred.
   */
  const handleSave = async (acknowledgeImpact = false) => {
    if (!companyId || !rules || !form || hasErrors) return;
    setSaving(true);
    setError(null);
    setApproverBlock(null);
    try {
      const input: CompanyRulesInput = {
        ...rules,
        payrollUseAttendanceOvertime: form.useAttendanceOvertime,
        payrollOvertimeBaseMode: form.overtimeBaseMode,
        payrollOvertimeRegularMultiplier: Number(form.regularMultiplier),
        payrollOvertimePremiumMultiplier: Number(form.premiumMultiplier),
        payrollHoursPerMonth: Number(form.hoursPerMonth),
        // CO-304. Back to "not configured" needs the clear flag: sending null would only mean
        // "field omitted" and the stored value would survive. The flag wins server-side, so the
        // value sent alongside it is irrelevant; it is sent as null for honesty.
        payrollRequireOvertimeApproval: form.requireApproval,
        clearPayrollRequireOvertimeApproval: form.requireApproval === null ? true : undefined,
        // CO-348. Same tristate contract, same clear flag.
        payrollNoManagerOvertimeApproval: form.noManagerRouting,
        clearPayrollNoManagerOvertimeApproval: form.noManagerRouting === null ? true : undefined,
        // Never pre-emptive: only what the dialog asked for, and only while arming.
        acknowledgeOvertimeApprovalGateImpact: arming && acknowledgeImpact ? true : undefined,
      };
      const result = await updateCompanyRules(companyId, input);
      // A 200 carrying an errors envelope. The 400 path rejects instead — see the catch.
      if (result.errors?.length) {
        if (!routeRefusal(result)) {
          setError(
            t('settings.payrollBasis.saveFailed', 'Could not save the payroll calculation basis.')
          );
        }
        return;
      }
      setImpactMessage(null);
      // The CO-304/CO-348 flags are per-request instructions, not stored state. Keeping them on the
      // round-trip copy would silently acknowledge (or re-clear) on every later save from this page.
      const persisted: CompanyRulesInput = { ...input };
      delete persisted.clearPayrollRequireOvertimeApproval;
      delete persisted.clearPayrollNoManagerOvertimeApproval;
      delete persisted.acknowledgeOvertimeApprovalGateImpact;
      // Keep the Redux company-rules copy in sync. id/companyId are stripped so they cannot
      // overwrite company.id.
      const rulesOnly: Partial<CompanyRulesInput> = { ...persisted };
      delete rulesOnly.id;
      delete rulesOnly.companyId;
      dispatch(updateCompanyRulesAction(rulesOnly));
      setRules(persisted);
      setBaseline(form);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      // This is where every 400 lands, both gates included.
      if (!routeRefusal(err)) {
        console.error('Failed to save attendance pay settings:', err);
        setImpactMessage(null);
        setError(
          t('settings.payrollBasis.saveFailed', 'Could not save the payroll calculation basis.')
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (baseline) setForm(baseline);
    setSaved(false);
    setApproverBlock(null);
    setImpactMessage(null);
  };

  const modeLabel = (mode: PayrollSalaryBaseMode) =>
    ({
      [PayrollSalaryBaseMode.Auto]: t('settings.payrollBasis.modeAuto', 'Automatic'),
      [PayrollSalaryBaseMode.BasicOnly]: t('settings.payrollBasis.modeBasicOnly', 'Basic salary only'),
      [PayrollSalaryBaseMode.Gross]: t('settings.payrollBasis.modeGross', 'Total gross salary'),
    })[mode];

  const modeBody = (mode: PayrollSalaryBaseMode) =>
    ({
      [PayrollSalaryBaseMode.Auto]: t(
        'settings.payrollBasis.modeAutoBody',
        'Uses the component flagged as the basic salary. If nobody has flagged one, it falls back to the only salary component, then to total gross. Recommended, because it never pays zero.'
      ),
      [PayrollSalaryBaseMode.BasicOnly]: t(
        'settings.payrollBasis.modeBasicOnlyBody',
        'Uses only the component explicitly flagged as the basic salary, with no fallback. Strictest reading of the labour law.'
      ),
      [PayrollSalaryBaseMode.Gross]: t(
        'settings.payrollBasis.modeGrossBody',
        'Uses the full gross salary, allowances included. Pays more than the basic-only calculation.'
      ),
    })[mode];

  const routingLabel = (routing: OvertimeNoManagerRouting) =>
    ({
      [OvertimeNoManagerRouting.RouteToHr]: t(
        'settings.payrollBasis.noManagerRoutingHr',
        'HR approves it'
      ),
      [OvertimeNoManagerRouting.AutoApprove]: t(
        'settings.payrollBasis.noManagerRoutingAuto',
        'Approve it automatically'
      ),
    })[routing];

  const routingBody = (routing: OvertimeNoManagerRouting) =>
    ({
      [OvertimeNoManagerRouting.RouteToHr]: t(
        'settings.payrollBasis.noManagerRoutingHrBody',
        'The request goes to everyone holding the HR role, who approve or reject it exactly as a manager would. Nothing is paid until one of them decides.'
      ),
      [OvertimeNoManagerRouting.AutoApprove]: t(
        'settings.payrollBasis.noManagerRoutingAutoBody',
        'The overtime is approved the moment attendance records it, with nobody reviewing it. Payroll pays it in full, so use this only where the hours are already trusted.'
      ),
    })[routing];

  /** One rule line under a numeric field. Lights up green the moment the value satisfies it. */
  const rule = (ok: boolean, text: string) => (
    <li className="flex items-start gap-1.5">
      <AppIcon
        name={ok ? 'Check' : 'X'}
        size={13}
        className={cn('mt-0.5 shrink-0', ok ? 'text-emerald-600' : 'text-muted-foreground')}
      />
      <span className={cn(ok ? 'text-muted-foreground' : 'text-destructive')}>{text}</span>
    </li>
  );

  const noBasicConfigured = componentsKnown && basicComponents.length === 0;

  /** Vertical mode chooser. Rows, not a card grid: each carries a different amount of copy. */
  const modeChooser = (
    value: PayrollSalaryBaseMode,
    onChange: (m: PayrollSalaryBaseMode) => void,
    groupLabel: string,
    disabled: boolean
  ) => (
    <div role="radiogroup" aria-label={groupLabel} className="space-y-2">
      {BASE_MODES.map((mode) => {
        const selected = value === mode;
        return (
          <button
            key={mode}
            type="button"
            role="radio"
            aria-checked={selected}
            disabled={disabled}
            onClick={() => onChange(mode)}
            className={cn(
              'w-full rounded-lg border p-3 text-start transition-colors duration-150',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20',
              selected
                ? 'border-primary bg-primary/5'
                : 'border-border hover:border-muted-foreground/50 hover:bg-muted/40',
              disabled && 'cursor-not-allowed opacity-50'
            )}
          >
            <span className="flex items-center gap-2">
              <span
                className={cn(
                  'grid h-4 w-4 shrink-0 place-items-center rounded-full border',
                  selected ? 'border-primary' : 'border-input'
                )}
                aria-hidden="true"
              >
                {selected && <span className="h-2 w-2 rounded-full bg-primary" />}
              </span>
              <span
                className={cn('text-sm font-medium', selected ? 'text-primary' : 'text-foreground')}
              >
                {modeLabel(mode)}
              </span>
              {mode === PayrollSalaryBaseMode.Auto && (
                <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium leading-4 text-muted-foreground">
                  {t('settings.payrollBasis.recommendedTag', 'Recommended')}
                </span>
              )}
            </span>
            <span className="mt-1.5 block max-w-[68ch] ps-6 text-xs text-muted-foreground">
              {modeBody(mode)}
            </span>
          </button>
        );
      })}
    </div>
  );

  const pageTitle = t('settings.attendancePay.title', 'Overtime and attendance pay');
  const pageDescription = t(
    'settings.attendancePay.description',
    'How the hours your team clocks become pay: whether overtime reaches payroll, whether it has to be approved first, and the rate it is paid at.'
  );

  if (loading) {
    return (
      <PageLayout title={pageTitle} description={pageDescription} icon={Timer} accentColor="blue">
        <div className="flex justify-center rounded-xl border border-border bg-card py-12">
          <Spinner size="lg" />
        </div>
      </PageLayout>
    );
  }

  if (!companyId || !form) {
    return (
      <PageLayout title={pageTitle} description={pageDescription} icon={Timer} accentColor="blue">
        <div className="rounded-xl border border-border bg-card p-6 text-sm text-muted-foreground">
          {error ||
            t(
              'settings.payrollBasis.noCompany',
              'No company is selected, so the payroll calculation basis cannot be configured.'
            )}
        </div>
      </PageLayout>
    );
  }

  const overtimeOff = !form.useAttendanceOvertime;
  /** What the server would actually do today, so an unconfigured tenant sees its real behaviour. */
  const effectiveRouting = form.noManagerRouting ?? PAYROLL_BASIS_DEFAULTS.noManagerRouting;
  /** The routing decides nothing unless approval is required, and nothing at all with overtime off. */
  const routingLocked = overtimeOff || form.requireApproval !== true;

  return (
    <PageLayout title={pageTitle} description={pageDescription} icon={Timer} accentColor="blue">
      <section aria-labelledby="attendance-pay-heading" className="space-y-4">
        <div className="overflow-hidden rounded-xl border border-border bg-card">
          <header className="border-b border-border bg-muted/40 px-5 py-4">
            <h3 id="attendance-pay-heading" className="text-base font-semibold text-foreground">
              {t('settings.attendancePay.sectionTitle', 'Overtime pay')}
            </h3>
            <p className="mt-1 max-w-[70ch] text-sm text-muted-foreground">
              {t(
                'settings.attendancePay.sectionIntro',
                'These settings decide what every payslip pays for overtime. A change applies from the next payroll run.'
              )}
            </p>
          </header>

          {/* Step 1 — does attendance overtime reach payroll at all. Its own row so the consequence
              sits directly under the control. */}
          <div
            className={cn(
              'px-5 py-4 transition-colors duration-200',
              form.useAttendanceOvertime ? 'bg-primary/[0.04]' : 'bg-amber-500/[0.06]'
            )}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <label
                    htmlFor="attendance-pay-use-attendance"
                    className="cursor-pointer text-sm font-semibold text-foreground"
                  >
                    {t('settings.payrollBasis.useAttendance', 'Pay overtime from attendance records')}
                  </label>
                  <span
                    className={cn(
                      'rounded-full px-2 py-0.5 text-[11px] font-medium leading-4',
                      form.useAttendanceOvertime
                        ? 'bg-primary/10 text-primary'
                        : 'bg-amber-500/15 text-amber-700 dark:text-amber-400'
                    )}
                  >
                    {form.useAttendanceOvertime
                      ? t('settings.payrollBasis.feeding', 'Feeding payroll')
                      : t('settings.payrollBasis.notFeeding', 'Not feeding payroll')}
                  </span>
                </div>
                <p className="mt-1.5 max-w-[70ch] text-sm text-muted-foreground">
                  {t(
                    'settings.payrollBasis.useAttendanceBody',
                    'When this is on, the overtime hours your team clocked are the hours payroll pays for.'
                  )}
                </p>
              </div>
              <Switch
                id="attendance-pay-use-attendance"
                checked={form.useAttendanceOvertime}
                onCheckedChange={(v) => patch({ useAttendanceOvertime: v })}
                disabled={saving}
              />
            </div>

            {overtimeOff && (
              <div className="mt-3 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200">
                <AppIcon name="AlertTriangle" size={16} className="mt-0.5 shrink-0" />
                <p className="max-w-[70ch]">
                  {t(
                    'settings.payrollBasis.useAttendanceOffWarning',
                    'Every payslip will show zero overtime hours, however much attendance recorded. Overtime then has to be paid by adding it manually as a variable pay.'
                  )}
                </p>
              </div>
            )}
          </div>

          {/* Step 2 — CO-304 approval requirement. The child of the switch above: it decides which
              of the hours that reach payroll are actually paid, so it sits directly under it and
              says plainly when the parent has made it inert. */}
          <div
            className={cn(
              'border-t border-border px-5 py-4 transition-opacity duration-200',
              overtimeOff && 'opacity-60'
            )}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <label
                    htmlFor="attendance-pay-require-approval"
                    className="cursor-pointer text-sm font-semibold text-foreground"
                  >
                    {t(
                      'settings.payrollBasis.requireApproval',
                      'Pay overtime only after it is approved'
                    )}
                  </label>
                  <span
                    className={cn(
                      'rounded-full px-2 py-0.5 text-[11px] font-medium leading-4',
                      form.requireApproval === true
                        ? 'bg-primary/10 text-primary'
                        : 'bg-muted text-muted-foreground'
                    )}
                  >
                    {form.requireApproval === true
                      ? t('settings.payrollBasis.approvalRequired', 'Approval required')
                      : form.requireApproval === false
                        ? t('settings.payrollBasis.approvalNotRequired', 'Approval not required')
                        : t('settings.payrollBasis.approvalDefault', 'Default: approval not required')}
                  </span>
                </div>
                <p className="mt-1.5 max-w-[70ch] text-sm text-muted-foreground">
                  {form.requireApproval === true
                    ? t(
                        'settings.payrollBasis.requireApprovalOnBody',
                        'Payroll pays approved overtime hours only. Hours nobody approved are not paid, including hours already worked.'
                      )
                    : t(
                        'settings.payrollBasis.requireApprovalOffBody',
                        'Off is the default: every overtime hour recorded by attendance is paid, whether or not anyone reviewed it.'
                      )}
                </p>
                {/* 🔴 CO-348 (re-verification P3) — TENSE FOLLOWS THE GATE. "Employees who have no
                    manager are approved automatically" is only true while approval is REQUIRED:
                    AttendanceService.RaiseOvertimeApprovalRequestAsync consults
                    `RequiresOvertimeApprovalOn` BEFORE the auto-approve branch (review D1), so with
                    the gate off a manager-less employee's shift falls through to the HR path and is
                    stamped Pending with no decider — verification measured exactly that. The
                    present indicative therefore asserted the one thing the gate makes false. Same
                    honest voice as `noManagerRoutingInertAuto` below: say what WILL happen, and say
                    what happens meanwhile. The RouteToHr line needs no such split — requests are
                    queued and decided whether or not the gate is armed; only the PAY consequence
                    waits, and `requireApprovalOffBody` above already states it. */}
                <p className="mt-1.5 max-w-[70ch] text-sm text-muted-foreground">
                  {effectiveRouting === OvertimeNoManagerRouting.AutoApprove
                    ? form.requireApproval === true
                      ? t(
                          'settings.payrollBasis.requireApprovalWhoDecidesAuto',
                          'The direct manager decides each request. Employees who have no manager are approved automatically.'
                        )
                      : t(
                          'settings.payrollBasis.requireApprovalWhoDecidesAutoPending',
                          "The direct manager decides each request. Employees who have no manager will be approved automatically once approval is required — until then their overtime is queued for HR like everyone else's."
                        )
                    : t(
                        'settings.payrollBasis.requireApprovalWhoDecides',
                        'The direct manager decides each request. HR decides for employees who have no manager.'
                      )}
                </p>
                <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1">
                  <Link
                    to="/attendance/reports/overtime"
                    className="inline-flex items-center gap-1 text-sm font-medium text-primary underline underline-offset-2"
                  >
                    {t(
                      'settings.payrollBasis.requireApprovalQueueLink',
                      'Open the overtime approval queue'
                    )}
                    <AppIcon name="ArrowRight" size={13} className="rtl:-scale-x-100" />
                  </Link>
                  {form.requireApproval !== null && (
                    <button
                      type="button"
                      disabled={saving}
                      onClick={() => patch({ requireApproval: null })}
                      className="text-sm font-medium text-muted-foreground underline underline-offset-2 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {t('settings.payrollBasis.requireApprovalRestoreDefault', 'Restore the default')}
                    </button>
                  )}
                </div>
                {overtimeOff && (
                  <p className="mt-2 text-xs text-muted-foreground">
                    {t(
                      'settings.payrollBasis.requireApprovalInert',
                      'Has no effect while the switch above is off, because no attendance overtime reaches payroll at all.'
                    )}
                  </p>
                )}
              </div>
              <Switch
                id="attendance-pay-require-approval"
                checked={form.requireApproval === true}
                onCheckedChange={(v) => patch({ requireApproval: v })}
                disabled={saving}
              />
            </div>

            {/* CO-348 — the one case the manager rule cannot answer. Nested under the requirement
                because it is only ever a consequence of it, and locked rather than hidden so the
                tenant can read what will happen before arming anything. Rows, not a switch: the
                two outcomes are different in kind, and an off-state that quietly means "HR" would
                hide a decision. */}
            <div
              className={cn(
                'mt-4 border-t border-border/70 pt-4 transition-opacity duration-200',
                routingLocked && 'opacity-60'
              )}
            >
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-sm font-semibold text-foreground">
                  {t(
                    'settings.payrollBasis.noManagerRoutingLabel',
                    'Employees who have no manager'
                  )}
                </p>
                {form.noManagerRouting === null && (
                  <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium leading-4 text-muted-foreground">
                    {t('settings.payrollBasis.noManagerRoutingDefaultTag', 'Default')}
                  </span>
                )}
              </div>
              {/* 🔴 CO-348 (re-verification P3, neighbour audit) — the same overstatement as the
                  line above, one paragraph down: "Overtime cannot be paid until somebody approves
                  it" is FALSE while the gate is off, and owner decision E6 keeps it off on every
                  tenant through go-live — so this sentence was wrong everywhere it rendered.
                  ReportsService (CO-306) pays every outside-schedule hour under a disarmed gate and
                  only subtracts REJECTED ones; nothing waits on an approval. Conditional mood while
                  the gate is not armed, present indicative once it is. */}
              <p className="mt-1.5 max-w-[70ch] text-sm text-muted-foreground">
                {form.requireApproval === true
                  ? t(
                      'settings.payrollBasis.noManagerRoutingBody',
                      'Overtime cannot be paid until somebody approves it, so an employee whose manager is not set needs a rule of their own.'
                    )
                  : t(
                      'settings.payrollBasis.noManagerRoutingBodyOff',
                      'Once overtime has to be approved, it cannot be paid until somebody approves it — so an employee whose manager is not set needs a rule of their own.'
                    )}
              </p>

              <div
                role="radiogroup"
                aria-label={t(
                  'settings.payrollBasis.noManagerRoutingLabel',
                  'Employees who have no manager'
                )}
                className="mt-3 space-y-2"
              >
                {NO_MANAGER_ROUTINGS.map((routing) => {
                  // An unconfigured tenant is shown the option that is actually in force, so the
                  // group never reads as "nothing is selected" for behaviour that already exists.
                  const selected = effectiveRouting === routing;
                  return (
                    <button
                      key={routing}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      disabled={saving || routingLocked}
                      onClick={() => patch({ noManagerRouting: routing })}
                      className={cn(
                        'w-full rounded-lg border p-3 text-start transition-colors duration-150',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20',
                        selected
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-muted-foreground/50 hover:bg-muted/40',
                        (saving || routingLocked) && 'cursor-not-allowed opacity-50'
                      )}
                    >
                      <span className="flex items-center gap-2">
                        <span
                          className={cn(
                            'grid h-4 w-4 shrink-0 place-items-center rounded-full border',
                            selected ? 'border-primary' : 'border-input'
                          )}
                          aria-hidden="true"
                        >
                          {selected && <span className="h-2 w-2 rounded-full bg-primary" />}
                        </span>
                        <span
                          className={cn(
                            'text-sm font-medium',
                            selected ? 'text-primary' : 'text-foreground'
                          )}
                        >
                          {routingLabel(routing)}
                        </span>
                      </span>
                      <span className="mt-1.5 block max-w-[68ch] ps-6 text-xs text-muted-foreground">
                        {routingBody(routing)}
                      </span>
                    </button>
                  );
                })}
              </div>

              <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1">
                {form.noManagerRouting !== null && (
                  <button
                    type="button"
                    disabled={saving || routingLocked}
                    onClick={() => patch({ noManagerRouting: null })}
                    className="text-sm font-medium text-muted-foreground underline underline-offset-2 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {t('settings.payrollBasis.requireApprovalRestoreDefault', 'Restore the default')}
                  </button>
                )}
                {/* 🔴 CO-348 (re-verification P3) — "Decides nothing" is true of PAY and of the
                    approval queue, but NOT of everything: a stored AutoApprove still pre-disables
                    Gate 1 on this tenant's NEXT arming request (CompanyService.cs
                    `noManagerOvertimeAutoApproves` -> `undecidableEmployees = 0`). So while the
                    control is locked AND the saved value is AutoApprove, say that instead of
                    overstating the inertness. */}
                {routingLocked && (
                  <p className="text-xs text-muted-foreground">
                    {effectiveRouting === OvertimeNoManagerRouting.AutoApprove
                      ? t(
                          'settings.payrollBasis.noManagerRoutingInertAuto',
                          'Approves nothing yet — but while this is saved, turning on overtime approval will not be blocked for employees who have no manager.'
                        )
                      : overtimeOff
                        ? t(
                            'settings.payrollBasis.requireApprovalInert',
                            'Has no effect while the switch above is off, because no attendance overtime reaches payroll at all.'
                          )
                        : t(
                            'settings.payrollBasis.noManagerRoutingInert',
                            'Decides nothing until overtime has to be approved.'
                          )}
                  </p>
                )}
              </div>
            </div>

            {/* Gate 1. The server measured this; it is an instruction, so it stays on screen until
                the user changes something rather than disappearing like a toast. */}
            {approverBlock && (
              <div className="mt-3 flex items-start gap-2 rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
                <AppIcon name="AlertTriangle" size={16} className="mt-0.5 shrink-0" />
                <div className="max-w-[70ch] space-y-1.5">
                  <p className="font-medium">
                    {t(
                      'settings.payrollBasis.approverBlockTitle',
                      'Fix this first: some employees have nobody who can approve their overtime.'
                    )}
                  </p>
                  <p>{approverBlock}</p>
                  {/* The other way out, and the reason CO-348 exists: the server drops this gate
                      entirely once nobody has to decide. Offered, never applied for them. */}
                  {effectiveRouting === OvertimeNoManagerRouting.RouteToHr && (
                    <p>
                      {t(
                        'settings.payrollBasis.approverBlockAutoHint',
                        'Alternatively, set the employees who have no manager to be approved automatically above. Their overtime is then paid without anyone reviewing it.'
                      )}
                    </p>
                  )}
                  <Link
                    to="/core-hr/directory"
                    className="inline-flex items-center gap-1 font-medium underline underline-offset-2"
                  >
                    {t('settings.payrollBasis.approverBlockLink', 'Open the employee directory')}
                    <AppIcon name="ArrowRight" size={13} className="rtl:-scale-x-100" />
                  </Link>
                </div>
              </div>
            )}
          </div>

          {/* Step 3 — what the hours are priced from. Dimmed and locked while the master switch is
              off, so the effect of turning it off is visible rather than hidden behind a
              disappearing section. */}
          <div
            className={cn(
              'border-t border-border px-5 py-5 transition-opacity duration-200',
              overtimeOff && 'opacity-60'
            )}
          >
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <h4 className="text-sm font-semibold text-foreground">
                {t('settings.payrollBasis.overtimeSection', 'Overtime rate')}
              </h4>
              {overtimeOff && (
                <span className="text-xs text-muted-foreground">
                  {t(
                    'settings.payrollBasis.overtimeSectionInert',
                    'Unused while the switch above is off.'
                  )}
                </span>
              )}
            </div>

            <p className="mt-3 text-sm font-medium text-foreground">
              {t('settings.payrollBasis.overtimeBaseLabel', 'Compute overtime from')}
            </p>
            <div className="mt-2">
              {modeChooser(
                form.overtimeBaseMode,
                (m) => patch({ overtimeBaseMode: m }),
                t('settings.payrollBasis.overtimeBaseLabel', 'Compute overtime from'),
                saving || overtimeOff
              )}
            </div>

            {form.overtimeBaseMode === PayrollSalaryBaseMode.BasicOnly && noBasicConfigured && (
              <div className="mt-3 flex items-start gap-2 rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
                <AppIcon name="AlertTriangle" size={16} className="mt-0.5 shrink-0" />
                <div className="max-w-[70ch] space-y-1.5">
                  <p className="font-medium">
                    {t(
                      'settings.payrollBasis.strictZeroTitle',
                      'With these settings overtime pays nothing.'
                    )}
                  </p>
                  <p>
                    {t(
                      'settings.payrollBasis.strictZeroBody',
                      'No component is flagged as the basic salary, and this mode has no fallback, so the overtime base is zero for every employee. Flag a component first, or switch to Automatic.'
                    )}
                  </p>
                  <Link
                    to="/settings/payroll/gross-pays"
                    className="inline-flex items-center gap-1 font-medium underline underline-offset-2"
                  >
                    {t('settings.payrollBasis.strictZeroLink', 'Open Gross Pays')}
                    <AppIcon name="ArrowRight" size={13} className="rtl:-scale-x-100" />
                  </Link>
                </div>
              </div>
            )}

            {form.overtimeBaseMode === PayrollSalaryBaseMode.BasicOnly && !noBasicConfigured && (
              <p className="mt-3 flex items-start gap-1.5 text-xs text-muted-foreground">
                <AppIcon name="Info" size={13} className="mt-0.5 shrink-0" />
                <span className="max-w-[70ch]">
                  {componentsKnown
                    ? t(
                        'settings.payrollBasis.strictBasisNamed',
                        'Overtime will be based on {{components}}. Nothing else counts towards the hourly rate.',
                        { components: basicComponents.map((c) => c.name).join(', ') }
                      )
                    : t(
                        'settings.payrollBasis.strictBasisUnknown',
                        'This mode has no fallback. If no component is flagged as the basic salary, overtime pays zero. Check the Gross Pays screen before saving.'
                      )}
                </span>
              </p>
            )}

            <div className="mt-5 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              <div>
                <label
                  htmlFor="attendance-pay-regular"
                  className="text-sm font-medium leading-none text-foreground"
                >
                  {t('settings.payrollBasis.regularMultiplier', 'Regular overtime multiplier')}
                </label>
                <input
                  id="attendance-pay-regular"
                  type="number"
                  step="0.05"
                  min={PAYROLL_OVERTIME_REGULAR_MIN}
                  inputMode="decimal"
                  className={cn(
                    INPUT_CLASSES,
                    'mt-2',
                    (!checks.regularNumeric || !checks.regularMin) &&
                      'border-destructive focus-visible:ring-destructive/40'
                  )}
                  value={form.regularMultiplier}
                  disabled={saving || overtimeOff}
                  aria-describedby="attendance-pay-regular-rules"
                  aria-invalid={!checks.regularNumeric || !checks.regularMin || undefined}
                  onChange={(e) => patch({ regularMultiplier: e.target.value })}
                />
                <ul id="attendance-pay-regular-rules" className="mt-2 space-y-1 text-xs">
                  {rule(checks.regularNumeric, t('settings.payrollBasis.ruleNumber', 'Is a number'))}
                  {rule(
                    checks.regularMin,
                    t('settings.payrollBasis.ruleRegularMin', 'At least 1.25, the statutory minimum')
                  )}
                </ul>
              </div>

              <div>
                <label
                  htmlFor="attendance-pay-premium"
                  className="text-sm font-medium leading-none text-foreground"
                >
                  {t('settings.payrollBasis.premiumMultiplier', 'Premium and holiday multiplier')}
                </label>
                <input
                  id="attendance-pay-premium"
                  type="number"
                  step="0.05"
                  min={PAYROLL_OVERTIME_PREMIUM_MIN}
                  inputMode="decimal"
                  className={cn(
                    INPUT_CLASSES,
                    'mt-2',
                    (!checks.premiumNumeric || !checks.premiumMin || !checks.premiumAboveRegular) &&
                      'border-destructive focus-visible:ring-destructive/40'
                  )}
                  value={form.premiumMultiplier}
                  disabled={saving || overtimeOff}
                  aria-describedby="attendance-pay-premium-rules"
                  aria-invalid={
                    !checks.premiumNumeric ||
                    !checks.premiumMin ||
                    !checks.premiumAboveRegular ||
                    undefined
                  }
                  onChange={(e) => patch({ premiumMultiplier: e.target.value })}
                />
                <ul id="attendance-pay-premium-rules" className="mt-2 space-y-1 text-xs">
                  {rule(checks.premiumNumeric, t('settings.payrollBasis.ruleNumber', 'Is a number'))}
                  {rule(
                    checks.premiumMin,
                    t('settings.payrollBasis.rulePremiumMin', 'At least 1.50, the statutory minimum')
                  )}
                  {rule(
                    checks.premiumAboveRegular,
                    t(
                      'settings.payrollBasis.rulePremiumAboveRegular',
                      'Not below the regular multiplier'
                    )
                  )}
                </ul>
              </div>

              <div>
                <label
                  htmlFor="attendance-pay-hours"
                  className="text-sm font-medium leading-none text-foreground"
                >
                  {t('settings.payrollBasis.hoursPerMonth', 'Working hours per month')}
                </label>
                <input
                  id="attendance-pay-hours"
                  type="number"
                  step="1"
                  min={1}
                  inputMode="numeric"
                  className={cn(
                    INPUT_CLASSES,
                    'mt-2',
                    (!checks.hoursWhole || !checks.hoursPositive) &&
                      'border-destructive focus-visible:ring-destructive/40'
                  )}
                  value={form.hoursPerMonth}
                  disabled={saving || overtimeOff}
                  aria-describedby="attendance-pay-hours-rules"
                  aria-invalid={!checks.hoursWhole || !checks.hoursPositive || undefined}
                  onChange={(e) => patch({ hoursPerMonth: e.target.value })}
                />
                <ul id="attendance-pay-hours-rules" className="mt-2 space-y-1 text-xs">
                  {rule(
                    checks.hoursPositive,
                    t('settings.payrollBasis.ruleHoursPositive', 'Greater than zero')
                  )}
                  {rule(checks.hoursWhole, t('settings.payrollBasis.ruleHoursWhole', 'A whole number'))}
                </ul>
                <p className="mt-2 max-w-[60ch] text-xs text-muted-foreground">
                  {t(
                    'settings.payrollBasis.hoursPerMonthHint',
                    'Divides the monthly salary base into an hourly rate. A larger number lowers the hourly rate.'
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Where the statutory half of payroll configuration lives. Rendered only when that route
            exists for this tenant. */}
        {complianceReachable && (
          <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground">
            <AppIcon name="Info" size={14} className="shrink-0" />
            <span className="max-w-[70ch]">
              {t(
                'settings.attendancePay.complianceHint',
                'Contribution schedules and the end-of-service salary base are statutory settings and live separately.'
              )}
            </span>
            <Link
              to="/settings/payroll/compliance"
              className="font-medium text-primary underline underline-offset-2"
            >
              {t('settings.attendancePay.complianceLink', 'Open payroll compliance settings')}
            </Link>
          </p>
        )}

        {error && (
          <div className="flex items-start gap-2 rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
            <AppIcon name="AlertCircle" size={16} className="mt-0.5 shrink-0" />
            {error}
          </div>
        )}

        {dirty && hasErrors && (
          <p className="flex items-center gap-1.5 text-sm text-destructive">
            <AppIcon name="AlertCircle" size={15} className="shrink-0" />
            {t(
              'settings.payrollBasis.blockedBySelf',
              'Saving is held back until the values marked above are corrected.'
            )}
          </p>
        )}

        {saved && (
          <p className="flex items-center gap-1.5 text-sm text-emerald-600 dark:text-emerald-400">
            <AppIcon name="Check" size={15} className="shrink-0" />
            {t('settings.payrollBasis.saved', 'Payroll calculation basis saved.')}
          </p>
        )}

        {/* Gate 2 — the confirmation step. The body is the server's own measured message, rendered
            as sent; only the chrome is translated. Confirming is the ONLY path that sends
            acknowledgeOvertimeApprovalGateImpact. */}
        <Dialog
          open={!!impactMessage}
          onOpenChange={(open) => {
            if (!open && !saving) setImpactMessage(null);
          }}
        >
          <DialogContent
            size="lg"
            hideCloseButton={saving}
            onEscapeKeyDown={(e) => {
              if (saving) e.preventDefault();
            }}
            onPointerDownOutside={(e) => {
              if (saving) e.preventDefault();
            }}
          >
            <DialogHeader>
              <DialogTitle>
                {t(
                  'settings.payrollBasis.impactDialogTitle',
                  'This will stop paying overtime nobody approved'
                )}
              </DialogTitle>
              <DialogDescription>
                {t(
                  'settings.payrollBasis.impactDialogLead',
                  'Read what the system measured for this company before confirming. It applies to hours already worked as well as to future ones.'
                )}
              </DialogDescription>
            </DialogHeader>
            <DialogBody>
              <p className="whitespace-pre-line rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200">
                {impactMessage}
              </p>
              <Link
                to="/attendance/reports/overtime"
                className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-primary underline underline-offset-2"
              >
                {t(
                  'settings.payrollBasis.impactDialogReportLink',
                  'Open the overtime report for this period'
                )}
                <AppIcon name="ArrowRight" size={13} className="rtl:-scale-x-100" />
              </Link>
            </DialogBody>
            <DialogFooter>
              <Button variant="outline" disabled={saving} onClick={() => setImpactMessage(null)}>
                {t('settings.payrollBasis.impactDialogCancel', 'Keep paying all overtime')}
              </Button>
              <Button
                variant="destructive"
                loading={saving}
                disabled={saving}
                onClick={() => handleSave(true)}
              >
                {t('settings.payrollBasis.impactDialogConfirm', 'I understand: require approval')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <StickyActionBar
          hasUnsavedChanges={dirty && !hasErrors}
          isSaving={saving}
          onReset={handleReset}
          onSave={() => handleSave(false)}
          t={t as (key: string) => string}
          saveLabel={t('settings.attendancePay.save', 'Save overtime settings')}
        />
      </section>
    </PageLayout>
  );
};

export default AttendancePaySettings;
